import React, { useState, useMemo, useRef, useCallback } from "react";
import {
  Modal,
  TextField,
  Select,
  Checkbox,
  InlineStack,
  Button,
  Text,
  BlockStack,
  Box,
  SkeletonDisplayText,
  Popover,
  ActionList,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { Option, Property, GroupData, FieldType } from "../../types";
import {
  PlusIcon,
  XIcon,
  XSmallIcon,
} from "@medentic/polaris-icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";
import { TranslateLink } from "@shared/ui";
import { PropertyTypeRenderer } from "@shared/ui";

interface PropertiesModalProps {
  open: boolean;
  onClose: () => void;
  initialData?: Property;
}

const DraggableOption: React.FC<{
  option: Option;
  index: number;
  disabled: boolean;
  onRemoveOption: (index: number) => void;
  onChange: (index: number, key: keyof Option, value: string) => void;
  labelPlaceholder: string;
  valuePlaceholder: string;
}> = ({
  option,
  index,
  onRemoveOption,
  onChange,
  disabled,
  labelPlaceholder,
  valuePlaceholder,
}) => {
  return (
    <SortableResourceItem
      accessibilityLabel={`Option ${index + 1}`}
      dragHandleAccessibilityLabel={`Reorder option ${index + 1}`}
    >
      <InlineStack gap="100">
        <Box width="44%">
          <TextField
            label=""
            autoComplete="false"
            placeholder={labelPlaceholder}
            value={option.label}
            onChange={(value) => onChange(index, "label", value)}
            disabled={disabled}
          />
        </Box>
        <Box width="44%">
          <TextField
            label=""
            autoComplete="false"
            placeholder={valuePlaceholder}
            value={option.value}
            onChange={(value) => onChange(index, "value", value)}
            disabled={disabled}
          />
        </Box>
        <Button
          icon={XIcon}
          onClick={() => onRemoveOption(index)}
          variant="plain"
          tone="critical"
          disabled={disabled}
        />
      </InlineStack>
    </SortableResourceItem>
  );
};

const PropertiesModal: React.FC<PropertiesModalProps> = ({
  open,
  onClose,
  initialData,
}) => {
  const { t } = useTranslation();
  const { GET, POST, PUT } = useApiRequest();
  const params = useParams();
  const clinicId = params.clinicId;
  const queryClient = useQueryClient();
  const [popoverActive, setPopoverActive] = useState(false);

  const { data: groupsData = [], isLoading: isGroupsLoading } = useQuery<
    GroupData[]
  >({
    queryKey: ["patient-schema"],
    queryFn: () => GET(`/api/healthcare-companies/${clinicId}/patients/schema`),
  });

  const groups = useMemo(
    () => (groupsData.length ? groupsData[0]?.groups : []),
    [groupsData]
  );

  const groupOptions = useMemo(() => {
    return groups.map((group) => ({ label: group.label, value: group.name }));
  }, [groups]);

  const [groupId, setGroupId] = useState(
    initialData?.groupId ?? groupOptions[0]?.value ?? undefined
  );
  const [label, setLabel] = useState(initialData?.label ?? undefined);
  const [name, setName] = useState(initialData?.name ?? undefined);
  const [description, setDescription] = useState(
    initialData?.description ?? undefined
  );
  const [renderType, setRenderType] = useState<FieldType>(
    (initialData?.renderer?.id as FieldType) ?? "textInput"
  );

  const [renderPropsEnabled, setrenderPropsEnabled] = useState(
    !!initialData?.renderer?.properties
  );
  const [renderProps, setRenderProps] = useState(
    JSON.stringify(initialData?.renderer?.properties, null, 2) ?? undefined
  );
  const [defaultValueEnabled, setDefaultValueEnabled] = useState(
    !!initialData?.defaultValue
  );
  const [defaultValue, setDefaultValue] = useState(
    initialData?.defaultValue ?? undefined
  );
  const [hasUniqueValue, setHasUniqueValue] = useState(
    initialData?.hasUniqueValue ?? undefined
  );
  const [isRequired, setIsRequired] = useState(
    initialData?.isRequired ?? undefined
  );
  const [isShortlisted, setIsShortlisted] = useState(
    initialData?.isShortlisted ?? undefined
  );
  const [isInCreateForm, setIsInCreateForm] = useState(
    initialData?.isInCreateForm ?? undefined
  );
  const [sensitivityLevel, setSensitivityLevel] = useState<
    undefined | "PII" | "PHI" | "CI"
  >(initialData?.sensitivityLevel ?? undefined);
  const [comboboxOptions, setComboboxOptions] = useState<Option[]>(
    (initialData?.options ?? []).sort((a, b) => a.displayOrder - b.displayOrder)
  );

  const [errors, setErrors] = useState({
    groupId: "",
    label: "",
    name: "",
    renderProps: "",
    defaultValue: "",
    regexPattern: "",
    // sensitivityLevel: "",
  });

  const [regexEnabled, setRegexEnabled] = useState(
    !!initialData?.regexValueValidation
  );
  const [regexPattern, setRegexPattern] = useState(
    initialData?.regexValueValidation ?? undefined
  );

  const isEditing = !!initialData;
  const isReadOnly = isEditing && initialData?.isReadOnlyDefinition;

  const sensitivityOptions = [
    {
      label: t("properties.sensitivity.none"),
      value: undefined,
      description: t("properties.sensitivity.noneDescription"),
    },
    {
      label: t("properties.sensitivity.pii"),
      value: "PII",
      description: t("properties.sensitivity.piiDescription"),
    },
    {
      label: t("properties.sensitivity.phi"),
      value: "PHI",
      description: t("properties.sensitivity.phiDescription"),
    },
    {
      label: t("properties.sensitivity.ci"),
      value: "CI",
      description: t("properties.sensitivity.ciDescription"),
    },
  ];

  const optionSortableIdsRef = useRef(new WeakMap<object, string>());
  const optionSortableCounterRef = useRef(0);

  const getSortableOptionId = useCallback((option: Option) => {
    console.debug("[PropertiesModal::getSortableOptionId] resolve stable sortable id");

    const existingId = optionSortableIdsRef.current.get(option as object);

    if (existingId) {
      return existingId;
    }

    const nextId = `option-${optionSortableCounterRef.current++}`;
    optionSortableIdsRef.current.set(option as object, nextId);

    return nextId;
  }, []);

  const handleOptionsReorder = useCallback((updatedOptions: Option[]) => {
    console.debug("[PropertiesModal::handleOptionsReorder] apply reordered option list");

    const normalized = updatedOptions.map((option, idx) => {
      option.displayOrder = idx + 1;
      return option;
    });
    setComboboxOptions(normalized);
  }, []);

  const handleAddOption = () => {
    setComboboxOptions((prev) => [
      ...prev,
      { label: "", value: "", displayOrder: prev.length + 1 },
    ]);
  };

  const handleRemoveOption = (index: number) => {
    const updatedOptions = comboboxOptions.filter((_, i) => i !== index);
    updatedOptions.forEach((option, idx) => {
      option.displayOrder = idx + 1;
    });
    setComboboxOptions(updatedOptions);
  };

  const handleRemoveAll = () => {
    setComboboxOptions([]);
  };

  const handleRenderTypeChange = (selected: string) => {
    setRenderType(selected as FieldType);
    // Disable regex for specific field types
    if (
      selected === "combobox" ||
      selected === "combeBoxMultiple" ||
      selected === "dropZone" ||
      selected === "checkbox"
    ) {
      setRegexEnabled(false);
    }
  };

  const validateRegexPattern = (pattern: string | undefined): boolean => {
    if (!pattern) return true;
    try {
      new RegExp(pattern);
      return true;
    } catch {
      return false;
    }
  };

  const validateFields = () => {
    const newErrors = {
      groupId: !groupId ? t("propertiesModal.alert.groupRequired") : "",
      label: !label ? t("propertiesModal.alert.labelRequired") : "",
      name: !name
        ? t("common.requiredField", {
            field_name: t("propertiesModal.name"),
          })
        : "",
      renderProps:
        renderPropsEnabled && !renderProps
          ? t("common.requiredField", {
              field_name: t("propertiesModal.customRendererOptions"),
            })
          : "",
      defaultValue:
        defaultValueEnabled && !defaultValue
          ? t("common.requiredField", {
              field_name: t("propertiesModal.defaultValue"),
            })
          : "",
      regexPattern:
        regexEnabled && !regexPattern
          ? t("common.requiredField", {
              field_name: t("propertiesModal.regexPattern"),
            })
          : !validateRegexPattern(regexPattern)
          ? t("propertiesModal.invalidRegexPattern")
          : "",
      // sensitivityLevel: !sensitivityLevel
      //   ? t("propertiesModal.alert.sensitivityLevelRequired")
      //   : "",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const handleLabelBlur = () => {
    // Auto-generate 'name' from 'label' if empty
    if (!name && label) {
      const generatedName = label.toLowerCase().replace(/[^a-z0-9]/g, "_");
      setName(generatedName);
    }
  };

  // Mutations
  const updateMutation = useMutation({
    mutationFn: async (property: Property) => {
      return PUT(
        `/api/healthcare-companies/${clinicId}/patients/schema/properties/${initialData?.name}`,
        property
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient-schema-properties"],
      });
      queryClient.invalidateQueries({ queryKey: ["patient-schema"] });
      onClose();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (property: Property) => {
      return POST(
        `/api/healthcare-companies/${clinicId}/patients/schema/properties`,
        property
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient-schema-properties"],
      });
      queryClient.invalidateQueries({ queryKey: ["patient-schema"] });
      onClose();
    },
  });

  const handleSave = () => {
    if (!validateFields()) return;

    const property: Property = {
      id: initialData?.id ?? Date.now().toString(),
      groupId,
      label: label ?? "",
      name: name ?? "",
      description: description ?? undefined,
      renderType: renderType ?? undefined,
      renderProps: renderPropsEnabled
        ? JSON.parse(renderProps)
        : initialData?.renderer?.properties,
      defaultValue: defaultValueEnabled ? defaultValue : undefined,
      hasUniqueValue: hasUniqueValue ?? false,
      isRequired: isRequired ?? false,
      isShortlisted: isShortlisted ?? false,
      isInCreateForm: isInCreateForm ?? false,
      options: ["combobox", "combeBoxMultiple", "teamMembers"].includes(
        renderType
      )
        ? comboboxOptions
        : undefined,
      isMedenticDefined: initialData?.isMedenticDefined ?? false,
      isReadOnlyDefinition: initialData?.isReadOnlyDefinition ?? false,
      isHiddenDefinition: initialData?.isHiddenDefinition ?? false,
      regexValueValidation: regexEnabled ? regexPattern : undefined,
      sensitivityLevel: sensitivityLevel,
    };

    if (isEditing) {
      updateMutation.mutate(property);
    } else {
      createMutation.mutate(property);
    }
  };

  const hideRegEx = !(
    renderType === "email" ||
    renderType === "number" ||
    renderType === "textInput" ||
    renderType === "textArea"
  );

  return (
    <Modal
        open={open}
        onClose={onClose}
        title={
          isEditing
            ? t("propertiesModal.title.edit")
            : t("propertiesModal.title.create")
        }
        primaryAction={{
          content: isEditing ? t("common.update") : t("common.create"),
          onAction: handleSave,
          loading: createMutation.isPending || updateMutation.isPending,
        }}
        secondaryActions={[
          {
            content: t("common.cancel"),
            onAction: onClose,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            {isGroupsLoading ? (
              <SkeletonDisplayText />
            ) : (
              <Select
                label={t("propertiesModal.group")}
                options={groupOptions}
                value={groupId}
                onChange={(value) => setGroupId(value ?? "")}
                helpText={t("propertiesModal.groupHelpText")}
                error={errors.groupId}
                disabled={isReadOnly}
                requiredIndicator
              />
            )}

            <TextField
              autoComplete="false"
              label={t("propertiesModal.label")}
              value={label}
              onChange={setLabel}
              onBlur={handleLabelBlur}
              helpText={t("propertiesModal.labelHelpText")}
              error={errors.label}
              autoFocus
              requiredIndicator
            />

            <TextField
              autoComplete="false"
              label={t("propertiesModal.name")}
              value={name}
              onChange={setName}
              helpText={t("propertiesModal.nameHelpText")}
              error={errors.name}
              disabled={isReadOnly}
              requiredIndicator
            />

            <TextField
              autoComplete="false"
              label={t("propertiesModal.description")}
              value={description}
              onChange={setDescription}
              multiline={2}
              helpText={t("propertiesModal.descriptionHelpText")}
            />

            {/* Field Type */}
            <Select
              label={t("propertiesModal.fieldType")}
              requiredIndicator
              options={[
                {
                  label: t("properties.type.checkbox"),
                  value: "checkbox",
                },
                { label: t("properties.type.date"), value: "date" },
                {
                  label: t("properties.type.phoneNumber"),
                  value: "phoneNumber",
                },
                {
                  label: t("properties.type.number"),
                  value: "number",
                },
                {
                  label: t("properties.type.textInput"),
                  value: "textInput",
                },
                {
                  label: t("properties.type.textArea"),
                  value: "textArea",
                },
                { label: t("properties.type.email"), value: "email" },
                {
                  label: t("properties.type.combobox"),
                  value: "combobox",
                },
                {
                  label: t("properties.type.combeBoxMultiple"),
                  value: "combeBoxMultiple",
                },
                {
                  label: t("properties.type.dropZone"),
                  value: "dropZone",
                },
                {
                  label: t("properties.type.teamMembers"),
                  value: "teamMembers",
                },
              ]}
              value={renderType}
              onChange={handleRenderTypeChange}
              helpText={t("propertiesModal.fieldTypeHelpText")}
              disabled={isReadOnly}
            />
            {renderType === "combobox" && (
              <Box
                borderColor="border"
                borderWidth="025"
                borderStyle="solid"
                padding="200"
                borderRadius="200"
              >
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="h3">{t("propertiesModal.options")}</Text>
                    {!!comboboxOptions.length && (
                      <Button
                        onClick={handleRemoveAll}
                        icon={XSmallIcon}
                        variant="plain"
                        tone="critical"
                        disabled={isReadOnly}
                      >
                        {t("propertiesModal.removeAll")}
                      </Button>
                    )}
                  </InlineStack>
                  <SortableList
                    items={comboboxOptions}
                    idForItem={(option) => getSortableOptionId(option)}
                    onChange={handleOptionsReorder}
                    isItemDragDisabled={() => !!isReadOnly}
                    renderItem={(option, _context, index) => (
                      <DraggableOption
                        option={option}
                        index={index}
                        onRemoveOption={handleRemoveOption}
                        disabled={!!isReadOnly}
                        labelPlaceholder={t("propertiesModal.optionLabelPlaceholder")}
                        valuePlaceholder={t("propertiesModal.optionValuePlaceholder")}
                        onChange={(
                          idx,
                          key: keyof Option,
                          value: Option[keyof Option]
                        ) => {
                          const updatedOptions = [...comboboxOptions];
                          updatedOptions[idx][key] = value as never;
                          setComboboxOptions(updatedOptions);
                        }}
                      />
                    )}
                  />
                  <Box>
                    <Button
                      onClick={handleAddOption}
                      icon={PlusIcon}
                      disabled={isReadOnly}
                    >
                      {t("propertiesModal.addOption")}
                    </Button>
                  </Box>
                </BlockStack>
              </Box>
            )}

            {["combeBoxMultiple", "teamMembers"].includes(renderType) && (
              <Box
                borderColor="border"
                borderWidth="025"
                borderStyle="solid"
                padding="200"
                borderRadius="200"
              >
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="h3">{t("propertiesModal.optionsMultiple")}</Text>
                    {!!comboboxOptions.length && (
                      <Button
                        onClick={handleRemoveAll}
                        icon={XSmallIcon}
                        variant="plain"
                        tone="critical"
                        disabled={isReadOnly}
                      >
                        {t("propertiesModal.removeAll")}
                      </Button>
                    )}
                  </InlineStack>
                  <SortableList
                    items={comboboxOptions}
                    idForItem={(option) => getSortableOptionId(option)}
                    onChange={handleOptionsReorder}
                    isItemDragDisabled={() => !!isReadOnly}
                    renderItem={(option, _context, index) => (
                      <DraggableOption
                        option={option}
                        index={index}
                        onRemoveOption={handleRemoveOption}
                        disabled={!!isReadOnly}
                        labelPlaceholder={t("propertiesModal.optionLabelPlaceholder")}
                        valuePlaceholder={t("propertiesModal.optionValuePlaceholder")}
                        onChange={(
                          idx,
                          key: keyof Option,
                          value: Option[keyof Option]
                        ) => {
                          const updatedOptions = [...comboboxOptions];
                          updatedOptions[idx][key] = value as never;
                          setComboboxOptions(updatedOptions);
                        }}
                      />
                    )}
                  />
                  <Box>
                    <Button
                      onClick={handleAddOption}
                      icon={PlusIcon}
                      disabled={isReadOnly}
                    >
                      {t("propertiesModal.addOption")}
                    </Button>
                  </Box>
                </BlockStack>
              </Box>
            )}

            {/* --------------- CUSTOM OPTIONS --------------- */}
            <Checkbox
              label={t("propertiesModal.customRendererOptions")}
              checked={renderPropsEnabled}
              onChange={() => setrenderPropsEnabled(!renderPropsEnabled)}
              helpText={
                <TranslateLink
                  disabled={isReadOnly}
                  text={t("propertiesModal.customRendererOptionsHelpText")}
                />
              }
              disabled={isReadOnly}
            />
            {renderPropsEnabled && (
              <TextField
                autoComplete="false"
                label=""
                value={renderProps}
                onChange={setRenderProps}
                error={errors.renderProps}
                disabled={isReadOnly}
                multiline={5}
              />
            )}

            {/* --------------- DEFAULT VALUE --------------- */}
            {renderType !== "dropZone" && (
              <Checkbox
                label={t("propertiesModal.defaultValue")}
                checked={defaultValueEnabled}
                onChange={() => setDefaultValueEnabled(!defaultValueEnabled)}
                helpText={t("propertiesModal.defaultValueHelpText")}
                disabled={isReadOnly}
              />
            )}

            {defaultValueEnabled && (
              <PropertyTypeRenderer
                id={renderType}
                label={
                  renderType === "checkbox"
                    ? defaultValue === "true"
                      ? t("propertiesModal.defaultValueTrue")
                      : t("propertiesModal.defaultValueFalse")
                    : ""
                }
                value={defaultValue}
                onChange={setDefaultValue}
                helpText={errors.defaultValue}
                comboOptions={
                  comboboxOptions as unknown as Record<string, string>[]
                }
              />
            )}

            {!hideRegEx && (
              <Checkbox
                label={t("propertiesModal.regexPattern")}
                checked={regexEnabled}
                onChange={() => setRegexEnabled(!regexEnabled)}
                helpText={
                  <TranslateLink
                    disabled={isReadOnly}
                    text={t("propertiesModal.regexPatternHelpText")}
                  />
                }
                disabled={isReadOnly}
              />
            )}

            {regexEnabled && !hideRegEx && (
              <TextField
                autoComplete="false"
                label={""}
                value={regexPattern}
                onChange={setRegexPattern}
                error={errors.regexPattern}
                disabled={isReadOnly}
                placeholder={t("propertiesModal.regexPatternPlaceholder")}
              />
            )}

            <BlockStack gap="200">
              <Text
                as="p"
                variant="bodyMd"
                tone={isReadOnly ? "disabled" : undefined}
                requiredIndicator
              >
                {t("propertiesModal.sensitivityLevel")}
              </Text>
              <Popover
                active={popoverActive}
                activator={
                  <Button
                    onClick={() => setPopoverActive(!popoverActive)}
                    disclosure
                    disabled={isReadOnly}
                  >
                    {
                      sensitivityOptions.find(
                        (option) => option.value === sensitivityLevel
                      )?.label
                    }
                  </Button>
                }
                autofocusTarget="first-node"
                onClose={() => setPopoverActive(false)}
                preferredPosition="below"
                preferredAlignment="left"
              >
                <ActionList
                  actionRole="menuitem"
                  sections={[
                    {
                      items: sensitivityOptions.map((option) => ({
                        content: option.label,
                        helpText: option.description,
                        onAction: () => {
                          setSensitivityLevel(
                            option.value as undefined | "PII" | "PHI" | "CI"
                          );
                          setPopoverActive(false);
                        },
                      })),
                    },
                  ]}
                />
              </Popover>
            </BlockStack>
            <Checkbox
              label={t("propertiesModal.unique")}
              checked={hasUniqueValue}
              onChange={setHasUniqueValue}
              helpText={t("propertiesModal.uniqueHelpText")}
              disabled={isReadOnly}
            />
            <Checkbox
              label={t("propertiesModal.required")}
              checked={isRequired}
              onChange={setIsRequired}
              helpText={t("propertiesModal.requiredHelpText")}
            />
            <Checkbox
              label={t("propertiesModal.shortlisted")}
              checked={isShortlisted}
              onChange={setIsShortlisted}
              helpText={t("propertiesModal.shortlistedHelpText")}
            />
            <Checkbox
              label={t("propertiesModal.inCreateForm")}
              checked={isInCreateForm}
              onChange={setIsInCreateForm}
              helpText={t("propertiesModal.inCreateFormHelpText")}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
  );
};
export default PropertiesModal;
